import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  NormalizationProperties,
  NormalizationRates,
} from "@bitwarden/web-vault/app/models/types/normalization-types";
import { ReferenceDataCalculationClass } from "@bitwarden/web-vault/app/services/DataCalculationService/reference-data/reference-data-calculation.class";
import { IReferenceData } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { DateFormatPipe } from "@bitwarden/web-vault/app/pipes/date-format.pipe";
import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";

export class SymbolConversion {
  private _baseCurrency: string;
  protected logService: LogService;
  private referenceDataCalculation: ReferenceDataCalculationClass;

  constructor(baseCurrency: string, logger: LogService, referenceData: IReferenceData[]) {
    this._baseCurrency = baseCurrency;
    this.logService = logger;
    this.referenceDataCalculation = new ReferenceDataCalculationClass(
      this.logService,
      referenceData,
    );
  }

  /**
   * Return the calculated normalizingRates data for symbols.
   * The rate supplied to the function is the rate to get from the symbol to the base, but if the symbol is an equity
   * and not a currency, then we need to split the rate into two parts, one for the symbol and the rest for the currency
   * normalization.
   * @param fromSymbol
   * @param toSymbol
   * @param date
   * @param rate
   * @private
   */
  private checkForSymbolRate(
    fromSymbol: string,
    toSymbol: string,
    date: number,
    rate: number,
  ): NormalizationRates {
    const normalizedRates: NormalizationRates = {
      normalizingRate: rate,
    };
    if (isCurrency(fromSymbol)) {
      // add in the valueCurrency if it's a currency
      normalizedRates.valueCurrency = fromSymbol;
      return normalizedRates;
    } else {
      // The calculation is for an equity but cannot pass the unit test
      // comment it out first and fix it later
      return this.convertSymbolRateToCurrency(fromSymbol, toSymbol, date, rate);
    }
  }

  /**
   * Rate to convert 1 of symbol into the supplied base currency base
   * @param fromSymbol
   * @param toSymbol
   * @param date
   */
  private getConversionRate(fromSymbol: string, toSymbol: string, date: number): number {
    return this.referenceDataCalculation.calculateRate(fromSymbol, toSymbol, date);
  }

  /**
   * Given a quantity and a symbol, work out the normalised value for a given date using market data.
   * Normalisation is calculated to the base currency unless a different currency is supplied as parameter.
   *
   * @param quantity
   * @param date
   * @param symbol
   * @param currency
   */
  normalizeQuantity(
    quantity: number,
    date: Date,
    symbol: string,
    currency?: string,
  ): NormalizationProperties {
    // normalize to the base currency if the to symbol is not supplied
    if (!currency) {
      currency = this._baseCurrency;
    }

    let normalizedValue = quantity;

    const normalizedProperties: NormalizationProperties = {
      value: quantity,
      normalizedValue: normalizedValue,
      valueCurrency: symbol,
      normalizedCurrency: currency,
    };

    const unixDate = date.getTime();
    const rate = this.getConversionRate(symbol, currency, unixDate);

    if (rate) {
      normalizedValue = normalizedValue * rate;
      const normalizedProperties: NormalizationProperties = {
        value: quantity,
        normalizingRate: rate,
        normalizedValue: normalizedValue,
        normalizedCurrency: currency,
      };
      const normalizedRates = this.checkForSymbolRate(symbol, currency, unixDate, rate);
      Object.assign(normalizedProperties, normalizedRates);

      if (normalizedRates.symbolValue) {
        const newValue = quantity * normalizedRates.symbolValue;
        normalizedProperties.value = newValue;
      }

      return normalizedProperties;
    } else {
      this.logService.warning(
        "No normalisation on symbol " +
          symbol +
          " because symbol does not exist in the reference data",
      );
    }
    return normalizedProperties;
  }

  convertSymbolRateToCurrency(fromSymbol: string, toSymbol: string, date: number, rate: number) {
    const normalizedRates: NormalizationRates = {
      normalizingRate: rate,
    };

    let symbolReferenceData = this.referenceDataCalculation.getReferenceForBaseDate(
      fromSymbol,
      date,
    );
    if (symbolReferenceData.length > 0) {
      const possibleCurrencies = symbolReferenceData.map((referenceData) => referenceData.symbol);
      let referenceData: IReferenceData;
      let symbolRate: number;
      let valueCurrency;
      if (possibleCurrencies.includes(toSymbol)) {
        //symbolRate = symbolReferenceData.symbols[base];
        referenceData = symbolReferenceData.find(
          (referenceData) => referenceData.symbol === toSymbol,
        );
        symbolRate = referenceData.rate;
        valueCurrency = referenceData.symbol;
      } else {
        // take the first currency as the one we are using
        referenceData = symbolReferenceData[0];
        symbolRate = referenceData.rate;
        valueCurrency = referenceData.symbol;
      }
      const updatedRate = rate / symbolRate;

      normalizedRates.symbolValue = symbolRate;
      normalizedRates.valueCurrency = valueCurrency;
      normalizedRates.normalizingRate = updatedRate;
      return normalizedRates;
    }
    // look for the symbol as a symbol in reference data
    symbolReferenceData = this.referenceDataCalculation.getReferenceForSymbolDate(fromSymbol, date);

    // try to get the symbol rate in the currency of the transaction
    let referenceData: IReferenceData;
    let symbolRate;
    let valueCurrency;
    if (symbolReferenceData) {
      referenceData = symbolReferenceData.find((referenceData) => referenceData.base === toSymbol);
      symbolRate = referenceData.rate;
      valueCurrency = referenceData.base;
      symbolRate = 1 / symbolRate;

      const updatedRate = rate / symbolRate;

      normalizedRates.symbolValue = symbolRate;
      normalizedRates.valueCurrency = valueCurrency;
      normalizedRates.normalizingRate = updatedRate;
    }
    return normalizedRates;
  }
}
